import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { IMAGES, PHONE, PHONE_HREF, scrollToId } from "@/lib/site";

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <section id="start" ref={ref} className="relative flex min-h-screen items-center overflow-hidden">
            <motion.div className="absolute inset-0" style={{ y: bgY }}>
                <img
                    src={IMAGES.hero}
                    alt="Gestapeltes Brennholz in warmem Licht"
                    className="h-[120%] w-full object-cover"
                />
                <div className="absolute inset-0 bg-black/45" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#120E09] via-[#120E09]/40 to-[#120E09]/70" />
            </motion.div>

            <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-36 text-center">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.15, duration: 0.8 }}
                    className="mb-8 text-xs font-semibold uppercase tracking-[0.25em] text-[#C88A45]"
                    data-testid="hero-overline"
                >
                    Brennholz · Bauholz · Forstdienstleistungen · Holzkunst
                </motion.p>

                <h1 className="flex justify-center overflow-hidden">
                    <motion.img
                        src="/logo-hero.png"
                        alt="KischioWood — Holz- und Forstarbeiten"
                        data-testid="hero-logo"
                        initial={{ y: "40%", opacity: 0, scale: 0.96 }}
                        animate={{ y: "0%", opacity: 1, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="w-full max-w-2xl"
                        style={{ filter: "drop-shadow(0 12px 32px rgba(0,0,0,0.55))" }}
                    />
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.8 }}
                    className="mx-auto mt-8 max-w-xl text-base text-[#D9D2C4] sm:text-lg"
                    data-testid="hero-subline"
                >
                    Holz aus der Region, für die Region. Geliefert mit dem 7,5-t-Lkw in die Uckermark,
                    nach Berlin, an die Mecklenburgische Seenplatte und nach Mecklenburg-Strelitz —
                    ab 50&nbsp;€ pro Schüttraummeter. Abholung in Templin nach Absprache möglich.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.8 }}
                    className="mt-10 flex flex-wrap items-center justify-center gap-4"
                >
                    <button
                        data-testid="hero-calc-button"
                        onClick={() => scrollToId("preise")}
                        className="rounded-full bg-[#C88A45] px-7 py-3 text-sm font-bold uppercase tracking-wide text-[#120E09] transition-[background-color,transform] duration-200 hover:bg-[#DBA057] active:scale-95"
                    >
                        Preis berechnen
                    </button>
                    <a
                        data-testid="hero-phone-link"
                        href={PHONE_HREF}
                        className="flex items-center gap-2 rounded-full border border-white/15 px-7 py-3 text-sm font-semibold text-[#EDE6DA] transition-[border-color,background-color] duration-200 hover:border-[#C88A45]/50 hover:bg-white/5"
                    >
                        <Phone size={15} /> {PHONE}
                    </a>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6 }}
                className="absolute bottom-8 right-6 z-10 hidden text-[#9C917D] md:block"
            >
                <ArrowDown size={20} className="animate-bounce" />
            </motion.div>
        </section>
    );
};
